import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ExternalLink, Mail, Chrome, Zap, MessageSquare, Shield } from "lucide-react";

const Login = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I log in to DeepSeek?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Visit chat.deepseek.com and click 'Sign In'. You can log in with your email address or Google account. Registration is free and takes less than a minute."
        }
      },
      {
        "@type": "Question",
        "name": "Is DeepSeek login free?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, DeepSeek is completely free to use. There are no subscription fees or message limits. Create a free account and start chatting immediately."
        }
      },
      {
        "@type": "Question",
        "name": "Can I use DeepSeek without creating an account?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You need an account to use DeepSeek Chat. This allows you to save your chat history and access all features. Registration is quick and free."
        }
      }
    ]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "DeepSeek Login Guide — Independent Sign-In Walkthrough",
    "description": "Independent guide explaining how to sign in on DeepSeek's official site chat.deepseek.com with email, Google or Apple, plus account recovery.",
    "url": "https://deepseek.ai/login"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://deepseek.ai" },
      { "@type": "ListItem", "position": 2, "name": "Login", "item": "https://deepseek.ai/login" }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Head>
        <title>How to Log In to DeepSeek: Step-by-Step Guide (2026)</title>
        <meta name="description" content="Independent guide to signing in at the official chat.deepseek.com: email, Google or Apple, account recovery and API keys. We link to DeepSeek — we do not host the login." />
        <link rel="canonical" href="https://deepseek.ai/login" />
        <meta property="og:title" content="How to Log In to DeepSeek: Step-by-Step Guide (2026)" />
        <meta property="og:description" content="Independent walkthrough of the official DeepSeek sign-in: email, Google or Apple, account recovery and API keys. The login itself happens on chat.deepseek.com." />
        <meta property="og:url" content="https://deepseek.ai/login" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How to Log In to DeepSeek: Step-by-Step Guide (2026)" />
        <meta name="twitter:description" content="Independent walkthrough of the official DeepSeek sign-in, account recovery and API keys. The login itself happens on chat.deepseek.com." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Head>

      <main className="container mx-auto px-4 py-8">
        <Breadcrumbs currentPage="Login" />

        {/* Hero Section */}
        <section className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">DeepSeek Login Guide</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4">
            This is an independent guide, not a login screen. Signing in always happens on DeepSeek's own site,
            <a href="https://chat.deepseek.com/sign_in" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline"> chat.deepseek.com</a> —
            free, with email, Google or Apple. Below: the exact steps, account recovery, and what to do when sign-in fails.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <Button size="lg" className="gap-2" asChild>
              <a href="https://chat.deepseek.com/sign_in" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-5 h-5" />
                Open official sign-in on chat.deepseek.com
              </a>
            </Button>
            <Button size="lg" variant="outline" className="gap-2" asChild>
              <a href="https://chat.deepseek.com/sign_up" target="_blank" rel="noopener noreferrer">
                Create an account on chat.deepseek.com
              </a>
            </Button>
          </div>
        </section>

        {/* Chat intent */}
        <section className="mb-16 max-w-4xl mx-auto">
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader>
              <MessageSquare className="w-10 h-10 text-primary mb-2" />
              <CardTitle>Looking for DeepSeek Chat, not the login screen?</CardTitle>
              <CardDescription>
                Signing in is only the first step. Most people searching for DeepSeek chat want to know
                what the chat can actually do, which model answers, and whether they can try it without an account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-muted-foreground">
                <li>
                  <Link to="/chat" className="text-primary hover:underline font-medium">Try the free demo chat on this site</Link>{" "}
                  — no account needed, runs on a community route with limited capacity.
                </li>
                <li>
                  <Link to="/blog/ai-chat" className="text-primary hover:underline font-medium">What DeepSeek Chat can do</Link>{" "}
                  — features, limits and how it compares to the paid API.
                </li>
                <li>
                  <Link to="/deepseek-v4" className="text-primary hover:underline font-medium">Which model you get</Link>{" "}
                  — the V4 line is the current generation behind DeepSeek chat.
                </li>
                <li>
                  <Link to="/pricing" className="text-primary hover:underline font-medium">What it costs</Link>{" "}
                  — chat is free; the API is billed per token.
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Login Options */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">How to Log In</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <Mail className="w-10 h-10 text-primary mb-2" />
                <CardTitle>Email Login</CardTitle>
                <CardDescription>Sign in with your email address</CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-muted-foreground mb-4">
                  <li>Go to <a href="https://chat.deepseek.com/sign_in" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">chat.deepseek.com</a></li>
                  <li>Enter your email address</li>
                  <li>Enter your password</li>
                  <li>Click "Sign In"</li>
                </ol>
                <p className="text-sm text-muted-foreground">
                  New user? <a href="https://chat.deepseek.com/sign_up" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Create a free account</a>
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Chrome className="w-10 h-10 text-primary mb-2" />
                <CardTitle>Google Login</CardTitle>
                <CardDescription>Sign in with your Google account</CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-muted-foreground mb-4">
                  <li>Go to <a href="https://chat.deepseek.com/sign_in" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">chat.deepseek.com</a></li>
                  <li>Click "Continue with Google"</li>
                  <li>Select your Google account</li>
                  <li>You're logged in!</li>
                </ol>
                <p className="text-sm text-muted-foreground">
                  Fastest way to get started – no password needed.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What You Get */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">What You Get with a DeepSeek Account</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center">
              <CardHeader>
                <Zap className="w-10 h-10 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">Free Access</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Unlimited chat with no subscription fees or message limits.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <MessageSquare className="w-10 h-10 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">Advanced Models</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Access DeepSeek V4-Flash and V4-Pro for reasoning-heavy tasks. The legacy V3 and R1 aliases were retired on 24 July 2026.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <Shield className="w-10 h-10 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">Saved History</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  All your conversations saved and accessible anytime.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <Chrome className="w-10 h-10 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">Chrome Extension</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Use our free extension for instant access while browsing.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How do I log in to DeepSeek?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Visit <a href="https://chat.deepseek.com/sign_in" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">chat.deepseek.com</a> and click "Sign In". 
                  You can log in with your email address or Google account. Registration is free and takes less than a minute.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Is DeepSeek login free?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes, DeepSeek is completely free to use. There are no subscription fees or message limits. 
                  Create a free account and start chatting immediately with advanced AI models.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I use DeepSeek without creating an account?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  You need an account to use DeepSeek Chat. This allows you to save your chat history and access all features. 
                  Registration is quick and free – just provide an email or use Google sign-in.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Is my data safe with DeepSeek?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  DeepSeek uses standard security protocols. Note that DeepSeek is a Chinese company, so data may be stored in China. 
                  For more details, see our <Link to="/is-deepseek-safe" className="text-primary hover:underline">DeepSeek safety guide</Link>.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-muted/50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Join millions of users already using DeepSeek AI. Free, powerful, and easy to use.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" asChild>
              <a href="https://chat.deepseek.com/sign_in" target="_blank" rel="noopener noreferrer">
                Sign In Now
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/chat">Learn More About DeepSeek Chat</Link>
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Login;